<template>
  <InputNumber
    v-model="model"
    v-bind="props"
    useGrouping
    mode="currency"
    :input-class="'w-full rounded-xl max-h-8 border border-[#BDBDBD] px-4 py-2 ' + inputClass"
    locale="ru"
    currency="RUB"
    @blur="emits('blur', $event)"
    @focus="emits('focus', $event)"
  />
</template>

<script setup lang="ts">
import { defineModel, withDefaults, defineProps, computed, watch } from 'vue'
import InputNumber, {
  type InputNumberBlurEvent,
  type InputNumberPassThroughOptions
} from 'primevue/inputnumber'

const props = withDefaults(
  defineProps<{
    invalid?: boolean
    readonly?: boolean
    disabled?: boolean
    placeholder?: string
    inputId?: string
    pt?: InputNumberPassThroughOptions

    max?: number
    min?: number
    minFractionDigits?: number
    maxFractionDigits?: number

    /** классы для InputNumber input */
    inputClass?: string

    /** значение по умолчанию в рублях (если v-model изначально null/undefined) */
    defaultValue?: number
  }>(),
  {
    minFractionDigits: 0
  }
)

const emits = defineEmits<{
  blur: [InputNumberBlurEvent]
  focus: [Event]
}>()

const model = defineModel<number | null>()

/** безопасные границы */
const minSafe = computed(() => props.min ?? 0)
const maxSafe = computed(() =>
  props.max != null ? Math.max(props.max, minSafe.value) : Number.MAX_SAFE_INTEGER
)

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max)

/** инициализация/синхронизация: если модель пустая — ставим defaultValue или min; если не пуста — поджимаем в границы */
watch(
  () => model.value,
  val => {
    if (val == null) {
      const base = props.defaultValue != null ? props.defaultValue : minSafe.value
      model.value = clamp(base, minSafe.value, maxSafe.value)
      return
    }
    const clamped = clamp(val, minSafe.value, maxSafe.value)
    if (clamped !== val) model.value = clamped
  },
  { immediate: true }
)

/** если границы поменялись — поджимаем значение; если модель была пустой — пересчитаем из defaultValue/min */
watch([minSafe, maxSafe], () => {
  const base =
    model.value == null ? (props.defaultValue != null ? props.defaultValue : minSafe.value) : model.value
  model.value = clamp(base, minSafe.value, maxSafe.value)
})
</script>
